import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy, Loader2, Share2 } from 'lucide-react'
import { toast } from 'sonner'

import { EID } from '@/lib/element-ids.registry'
import {
  useAuditShare,
  useCreateAuditShare,
  useDeactivateAuditShare,
} from '@/hooks/useListingBuilder'

interface ShareToggleProps {
  jobId: string
}

function shareUrlFor(token: string): string {
  return `${window.location.origin}/public/listing-builder/audit/${token}`
}

/** Activate/deactivate a public audit share link and copy it to the clipboard. */
export function ShareToggle({ jobId }: ShareToggleProps) {
  const { t, i18n } = useTranslation()
  const [copied, setCopied] = useState(false)
  const { data: share, isLoading } = useAuditShare(jobId)
  const create = useCreateAuditShare(jobId)
  const deactivate = useDeactivateAuditShare(jobId)

  const isActive = !!(share?.is_active)
  const isPending = create.isPending || deactivate.isPending

  const onError = () => toast.error(t('listingBuilder.share.error'))

  const handleToggle = async () => {
    try {
      if (isActive) {
        await deactivate.mutateAsync()
      } else {
        await create.mutateAsync(undefined)
      }
    } catch {
      onError()
    }
  }

  const handleCopy = async () => {
    const token = share?.share_token
    if (!token) return
    try {
      await navigator.clipboard.writeText(share?.share_url ?? shareUrlFor(token))
      setCopied(true)
      toast.success(t('listingBuilder.share.copied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      onError()
    }
  }

  if (isLoading) {
    return <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
  }

  const expiresLabel =
    isActive && share?.expires_at
      ? t('listingBuilder.share.expiresAt', {
          date: new Date(share.expires_at).toLocaleDateString(i18n.language),
        })
      : null

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-3">
      <button
        id={EID['listing-builder-share-toggle-btn']}
        type="button"
        onClick={handleToggle}
        disabled={isPending}
        className={`flex items-center gap-1.5 text-sm transition-colors disabled:opacity-50 ${
          isActive
            ? 'text-emerald-600 dark:text-emerald-400'
            : 'text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100'
        }`}
      >
        {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
        <span>{isActive ? t('listingBuilder.share.deactivate') : t('listingBuilder.share.activate')}</span>
      </button>

      {isActive && share && (
        <button
          id={EID['listing-builder-share-copy-btn']}
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
        >
          {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
          <span>{copied ? t('listingBuilder.share.copied') : t('listingBuilder.share.copy')}</span>
        </button>
      )}
      </div>
      {isActive && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          {t('listingBuilder.share.active')}
          {expiresLabel ? ` · ${expiresLabel}` : ''}
        </p>
      )}
    </div>
  )
}
